import { TypeArray, QuestionTarget, InteractionOrientation } from "../types"
import { reducerTypeGuard } from "../reducers/reducerGuards"
import { actionCreatorTypeGuard } from "../reducers/actionCreators"
import {
  processActionMeta,
  getObjectTypeReducerGuard
} from "../reducers"

export type Settings = {
  disable_feedback: boolean
  min_correct: number
  max_retries: number
  autosubmit: boolean
  autocomplete: boolean
  visible_blocks: Array<QuestionTarget>
  target: QuestionTarget | null
  disable_scoring: boolean
  timer: number
  shuffle: boolean
  orientation: InteractionOrientation
  show_zones: boolean
  only_zones: boolean
  disable_retry_feedback: boolean
  choice_start: string
  strict: boolean
  round_precision: number
  nodes: string[]
}

const reducerAction = {
  disable_feedback: actionCreatorTypeGuard<Settings, boolean>(
    "disable_feedback",
    "Settings"
  ),
  min_correct: actionCreatorTypeGuard<Settings, number>(
    "min_correct",
    "Settings"
  ),
  max_retries: actionCreatorTypeGuard<Settings, number>(
    "max_retries",
    "Settings"
  ),
  autosubmit: actionCreatorTypeGuard<Settings, boolean>(
    "autosubmit",
    "Settings"
  ),
  autocomplete: actionCreatorTypeGuard<Settings, boolean>(
    "autocomplete",
    "Settings"
  ),
  visible_blocks: actionCreatorTypeGuard<Settings, Array<QuestionTarget>>(
    "visible_blocks",
    "Settings"
  ),
  target: actionCreatorTypeGuard<Settings, QuestionTarget | null>(
    "target",
    "Settings"
  ),
  disable_scoring: actionCreatorTypeGuard<Settings, boolean>(
    "disable_scoring",
    "Settings"
  ),
  timer: actionCreatorTypeGuard<Settings, number>("timer", "Settings"),
  shuffle: actionCreatorTypeGuard<Settings, boolean>("shuffle", "Settings"),
  orientation: actionCreatorTypeGuard<Settings, string>(
    "orientation",
    "Settings"
  ),
  show_zones: actionCreatorTypeGuard<Settings, boolean>(
    "show_zones",
    "Settings"
  ),
  only_zones: actionCreatorTypeGuard<Settings, boolean>(
    "only_zones",
    "Settings"
  ),
  disable_retry_feedback: actionCreatorTypeGuard<Settings, boolean>(
    "disable_retry_feedback",
    "Settings"
  ),
  choice_start: actionCreatorTypeGuard<Settings, string>(
    "choice_start",
    "Settings"
  ),
  strict: actionCreatorTypeGuard<Settings, boolean>("strict", "Settings"),
  round_precision: actionCreatorTypeGuard<Settings, number>(
    "round_precision",
    "Settings"
  ),
  nodes: actionCreatorTypeGuard<Settings, Array<string>>("nodes", "Settings"),
  __record: actionCreatorTypeGuard<TypeArray, Settings>("Settings", "__record"),
  __create: actionCreatorTypeGuard<TypeArray, null | Partial<Settings>>(
    "create",
    "__create/Settings"
  ),
  __null: actionCreatorTypeGuard<TypeArray, null>("null", "__record")
}

// Type safety to ensure we have a defined action for each property
type ReducerActions = {
  [key in
    | keyof Settings
    | "__record"
    | "__null"
    | "__create"]: (typeof reducerAction)[key][0]
}

// Our exported actions
export const settingsAction = Object.fromEntries(
  Object.entries(reducerAction).map(k => [k[0], k[1][0]])
) as ReducerActions

export const settingsReducer = reducerTypeGuard<Settings>()
  .case(...reducerAction.disable_feedback)
  .case(...reducerAction.min_correct)
  .case(...reducerAction.max_retries)
  .case(...reducerAction.autocomplete)
  .case(...reducerAction.autosubmit)
  .case(...reducerAction.visible_blocks)
  .case(...reducerAction.target)
  .case(...reducerAction.disable_scoring)
  .case(...reducerAction.timer)
  .case(...reducerAction.shuffle)
  .case(...reducerAction.orientation)
  .case(...reducerAction.show_zones)
  .case(...reducerAction.only_zones)
  .case(...reducerAction.disable_retry_feedback)
  .case(...reducerAction.choice_start)
  .case(...reducerAction.strict)
  .case(...reducerAction.round_precision)
  .case(...reducerAction.nodes)
  .default((prevState, action: any) => {
    action = processActionMeta(action)

    switch (action?.meta?.prop) {
      case "target":
        return getObjectTypeReducerGuard<Settings, QuestionTarget | null>(
          "settings",
          "target",
          "QuestionTarget",
          prevState,
          action
        )
    }

    return prevState
  })
